import React, { useState } from 'react';
import { Key, Shield, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';

const ChangePasswordModal = ({ admin, onClose, onSubmit, isOwnAccount = false }) => {
    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState('');

    const checkPasswordStrength = (password) => {
        if (password.length === 0) return '';
        
        const hasLower = /[a-z]/.test(password);
        const hasUpper = /[A-Z]/.test(password);
        const hasNumber = /[0-9]/.test(password);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const isLong = password.length >= 8;

        const strength = [hasLower, hasUpper, hasNumber, hasSpecial, isLong].filter(Boolean).length;

        if (strength <= 2) return 'weak';
        if (strength <= 4) return 'medium';
        return 'strong';
    };

    const getPasswordStrengthColor = (strength) => {
        switch (strength) {
            case 'weak': return 'bg-red-500';
            case 'medium': return 'bg-yellow-500';
            case 'strong': return 'bg-green-500';
            default: return 'bg-gray-300';
        }
    };

    const getPasswordStrengthText = (strength) => {
        switch (strength) {
            case 'weak': return 'Weak password';
            case 'medium': return 'Medium strength';
            case 'strong': return 'Strong password';
            default: return 'Enter a password';
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        if (name === 'newPassword') {
            setPasswordStrength(checkPasswordStrength(value));
        }

        // Clear error when user starts typing
        if (error) setError('');
    };

    const validateForm = () => {
        if (isOwnAccount && !formData.currentPassword) {
            setError('Please enter your current password');
            return false;
        }

        if (!formData.newPassword) {
            setError('Please enter a new password');
            return false;
        }

        if (formData.newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return false;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setError('Passwords do not match');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        const actionText = isOwnAccount ? 
            'change your password' : 
            `change the password for ${admin.email}`;

        if (!window.confirm(`Are you sure you want to ${actionText}?`)) {
            return;
        }

        setLoading(true);
        try {
            await onSubmit(admin.id, formData.newPassword, isOwnAccount ? formData.currentPassword : undefined);
        } catch (error) {
            console.error('Error changing password:', error);
            setError('Failed to change password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const PasswordInput = ({ label, name, value, showPassword, onToggleVisibility, placeholder }) => (
        <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
                {label}
            </label>
            <div className="relative">
                <input
                    type={showPassword ? "text" : "password"}
                    name={name}
                    value={value}
                    onChange={handlePasswordChange}
                    required
                    minLength="6"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 pr-12"
                    placeholder={placeholder}
                />
                <button
                    type="button"
                    onClick={onToggleVisibility}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
            </div>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-2xl">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Key className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">
                                {isOwnAccount ? 'Change Your Password' : 'Change Admin Password'}
                            </h2>
                            <p className="text-sm text-gray-600 mt-1">
                                {isOwnAccount ? 
                                    'Update your account password' : 
                                    `Changing password for: ${admin.email}`
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Security Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start space-x-3">
                            <Shield className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                            <div>
                                <h3 className="text-sm font-semibold text-amber-800">
                                    Security Notice
                                </h3>
                                <div className="mt-1 text-sm text-amber-700">
                                    <p>
                                        {isOwnAccount ? 
                                            'You will be logged out and need to sign in again with your new password.' :
                                            'This will immediately change the admin\'s password. They will need to use the new password to log in.'
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Current Password (for own account) */}
                    {isOwnAccount && (
                        <PasswordInput
                            label="Current Password"
                            name="currentPassword"
                            value={formData.currentPassword}
                            showPassword={showCurrentPassword}
                            onToggleVisibility={() => setShowCurrentPassword(!showCurrentPassword)}
                            placeholder="Enter your current password"
                        />
                    )}

                    {/* New Password */}
                    <div>
                        <PasswordInput
                            label="New Password"
                            name="newPassword"
                            value={formData.newPassword}
                            showPassword={showNewPassword}
                            onToggleVisibility={() => setShowNewPassword(!showNewPassword)}
                            placeholder="Enter new password (min. 6 characters)"
                        />
                        
                        {/* Password Strength Indicator */}
                        {formData.newPassword && (
                            <div className="mt-3">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-gray-600">
                                        Password Strength
                                    </span>
                                    <span className={`text-xs font-semibold ${
                                        passwordStrength === 'weak' ? 'text-red-600' :
                                        passwordStrength === 'medium' ? 'text-yellow-600' :
                                        passwordStrength === 'strong' ? 'text-green-600' :
                                        'text-gray-600'
                                    }`}>
                                        {getPasswordStrengthText(passwordStrength)}
                                    </span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-2">
                                    <div 
                                        className={`h-2 rounded-full transition-all duration-300 ${
                                            getPasswordStrengthColor(passwordStrength)
                                        }`}
                                        style={{ 
                                            width: passwordStrength === 'weak' ? '33%' : 
                                                   passwordStrength === 'medium' ? '66%' : 
                                                   passwordStrength === 'strong' ? '100%' : '0%' 
                                        }}
                                    />
                                </div>
                                <div className="mt-2 text-xs text-gray-500 space-y-1">
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle className={`h-3 w-3 ${
                                            formData.newPassword.length >= 6 ? 'text-green-500' : 'text-gray-300'
                                        }`} />
                                        <span>At least 6 characters</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle className={`h-3 w-3 ${
                                            /[A-Z]/.test(formData.newPassword) ? 'text-green-500' : 'text-gray-300'
                                        }`} />
                                        <span>Uppercase letter</span>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <CheckCircle className={`h-3 w-3 ${
                                            /[0-9]/.test(formData.newPassword) ? 'text-green-500' : 'text-gray-300'
                                        }`} />
                                        <span>Number</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Confirm Password */}
                    <PasswordInput
                        label="Confirm New Password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        showPassword={showConfirmPassword}
                        onToggleVisibility={() => setShowConfirmPassword(!showConfirmPassword)}
                        placeholder="Confirm your new password"
                    />

                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                            <div className="flex items-center space-x-3">
                                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                                <div>
                                    <p className="text-sm font-medium text-red-800">{error}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-6 py-3 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-colors duration-200"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 border border-transparent rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 transition-all duration-200 shadow-lg"
                        >
                            {loading ? (
                                <div className="flex items-center space-x-2">
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    <span>Changing Password...</span>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2">
                                    <Key className="h-4 w-4" />
                                    <span>Change Password</span>
                                </div>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ChangePasswordModal;